/** Static dungeon geometry: backdrop, flat room/corridor floors, walls and obstacles. */
import * as THREE from 'three';
import type { DungeonDef, Polygon } from '@keystone/sim';
import { computeWallSegments } from '../walls';
import { layFlat, toThree } from './coords';

const ROOM_TONES = [0x2c313b, 0x2f3540, 0x2a2f37, 0x313742];
const CORRIDOR_TONE = 0x262a31;
const WALL_HEIGHT = 2.4;
const WALL_THICKNESS = 0.4;

function polyShape(poly: Polygon): THREE.Shape {
  return new THREE.Shape(poly.points.map((p) => new THREE.Vector2(p.x, p.y)));
}

export function buildDungeon(d: DungeonDef): THREE.Group {
  const g = new THREE.Group();
  g.name = 'dungeon';

  const b = d.bounds;
  const margin = 40;
  const backdrop = new THREE.Mesh(new THREE.PlaneGeometry(b.w + margin * 2, b.h + margin * 2), new THREE.MeshLambertMaterial({ color: 0x0e1014 }));
  layFlat(backdrop);
  backdrop.position.copy(toThree({ x: b.x + b.w / 2, y: b.y + b.h / 2 }, -0.05));
  g.add(backdrop);

  d.rooms.forEach((r, i) => {
    const m = new THREE.Mesh(new THREE.ShapeGeometry(polyShape(r.shape)), new THREE.MeshLambertMaterial({ color: ROOM_TONES[i % ROOM_TONES.length] }));
    layFlat(m);
    m.name = `room:${r.id}`;
    g.add(m);
  });
  for (const c of d.corridors) {
    const m = new THREE.Mesh(new THREE.ShapeGeometry(polyShape(c.shape)), new THREE.MeshLambertMaterial({ color: CORRIDOR_TONE }));
    layFlat(m);
    m.position.y = 0.01;
    g.add(m);
  }

  const wallMat = new THREE.MeshLambertMaterial({ color: 0x4a5160 });
  const walkable = [...d.rooms.map((r) => r.shape), ...d.corridors.map((c) => c.shape)];
  for (const [a, c] of computeWallSegments(walkable)) {
    const len = Math.hypot(c.x - a.x, c.y - a.y);
    if (len < 0.05) continue;
    const wall = new THREE.Mesh(new THREE.BoxGeometry(len + WALL_THICKNESS, WALL_HEIGHT, WALL_THICKNESS), wallMat);
    wall.position.copy(toThree({ x: (a.x + c.x) / 2, y: (a.y + c.y) / 2 }, WALL_HEIGHT / 2));
    wall.rotation.y = Math.atan2(c.y - a.y, c.x - a.x);
    g.add(wall);
  }

  const obstacleMat = new THREE.MeshLambertMaterial({ color: 0x575f6e });
  for (const o of d.obstacles ?? []) {
    const m = new THREE.Mesh(new THREE.ExtrudeGeometry(polyShape(o), { depth: 3, bevelEnabled: false }), obstacleMat);
    layFlat(m);
    g.add(m);
  }

  // Entrance marker.
  const ent = new THREE.Mesh(new THREE.RingGeometry(1.2, 1.5, 32), new THREE.MeshBasicMaterial({ color: 0x6fd3ff, transparent: true, opacity: 0.5 }));
  layFlat(ent);
  ent.position.copy(toThree(d.entrance, 0.02));
  g.add(ent);
  return g;
}
